import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Campaign, AttachFile, Send } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import { formatDateTimeISO } from '../../../utils/dateUtils';
import type { AdminDialogProps } from '../types/adminTools.types';
import type {
  OrganizationsResponse,
  CreateAnnouncementResponse,
  UserWithOrganization,
  Organization,
  AnnouncementAdmin,
  AdminAnnouncementsResponse,
} from '../../../types/api';

type AnnouncementTargetType = 'all_users' | 'organizations' | 'users';

interface AnnouncementsDialogProps extends AdminDialogProps {
  // Sent announcements list
  announcementsData: AdminAnnouncementsResponse | null;
  announcementsLoading: boolean;
  announcementsError: string;
  // Targeting data (owned by parent, shared with org management)
  orgsData: OrganizationsResponse | null;
  availableUsers: UserWithOrganization[];
  // Form state
  announcementTitle: string;
  announcementBody: string;
  announcementFiles: File[];
  targetType: AnnouncementTargetType;
  selectedOrgs: string[];
  selectedUsers: string[];
  sendEmailNotification: boolean;
  sendingAnnouncement: boolean;
  sendAnnouncementResult: CreateAnnouncementResponse | null;
  sendAnnouncementError: string;
  // Form setters / callbacks
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onFilesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onTargetTypeChange: (value: AnnouncementTargetType) => void;
  onSelectedOrgsChange: (value: string[]) => void;
  onSelectedUsersChange: (value: string[]) => void;
  onSendEmailNotificationChange: (value: boolean) => void;
  onSend: () => Promise<void>;
}

export const AnnouncementsDialog: React.FC<AnnouncementsDialogProps> = ({
  open,
  onClose,
  announcementsData,
  announcementsLoading,
  announcementsError,
  orgsData,
  availableUsers,
  announcementTitle,
  announcementBody,
  announcementFiles,
  targetType,
  selectedOrgs,
  selectedUsers,
  sendEmailNotification,
  sendingAnnouncement,
  sendAnnouncementResult,
  sendAnnouncementError,
  onTitleChange,
  onBodyChange,
  onFilesChange,
  onRemoveFile,
  onTargetTypeChange,
  onSelectedOrgsChange,
  onSelectedUsersChange,
  onSendEmailNotificationChange,
  onSend,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid="announcements-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Campaign />
          {t('adminToolsPage.announcements.dialogTitle')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Send New Announcement Section */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Send />
              {t('adminToolsPage.announcements.sendNew')}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label={t('adminToolsPage.announcements.announcementTitle')}
                value={announcementTitle}
                onChange={(e) => onTitleChange(e.target.value)}
                disabled={sendingAnnouncement}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={t('adminToolsPage.announcements.messageBody')}
                value={announcementBody}
                onChange={(e) => onBodyChange(e.target.value)}
                disabled={sendingAnnouncement}
                multiline
                rows={4}
                sx={{ mb: 2 }}
              />

              {/* Target Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('adminToolsPage.announcements.sendTo')}</InputLabel>
                <Select
                  value={targetType}
                  onChange={(e) => onTargetTypeChange(e.target.value as AnnouncementTargetType)}
                  label={t('adminToolsPage.announcements.sendTo')}
                  disabled={sendingAnnouncement}
                >
                  <MenuItem value="all_users">
                    {t('adminToolsPage.announcements.targetTypes.allUsers')}
                  </MenuItem>
                  <MenuItem value="organizations">
                    {t('adminToolsPage.announcements.targetTypes.specificOrgs')}
                  </MenuItem>
                  <MenuItem value="users">
                    {t('adminToolsPage.announcements.targetTypes.specificUsers')}
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Organization Selection */}
              {targetType === 'organizations' && orgsData && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('adminToolsPage.announcements.selectOrganizations')}</InputLabel>
                  <Select
                    multiple
                    value={selectedOrgs}
                    onChange={(e) => onSelectedOrgsChange(e.target.value as string[])}
                    label={t('adminToolsPage.announcements.selectOrganizations')}
                    disabled={sendingAnnouncement}
                  >
                    {orgsData.organizations.map((org: Organization) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* User Selection */}
              {targetType === 'users' && availableUsers.length > 0 && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t('adminToolsPage.announcements.selectUsers')}</InputLabel>
                  <Select
                    multiple
                    value={selectedUsers}
                    onChange={(e) => onSelectedUsersChange(e.target.value as string[])}
                    label={t('adminToolsPage.announcements.selectUsers')}
                    disabled={sendingAnnouncement}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((userId) => {
                          const user = availableUsers.find((u) => u.id === userId);
                          return (
                            <Chip
                              key={userId}
                              label={user?.full_name || user?.email}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Checkbox checked={selectedUsers.includes(user.id)} />
                        <Box>
                          <Typography variant="body2">{user.full_name || user.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email} • {user.organization_name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* File Attachments */}
              <Box sx={{ mb: 2 }}>
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="announcement-files-input"
                  type="file"
                  multiple
                  onChange={onFilesChange}
                  disabled={sendingAnnouncement}
                />
                <label htmlFor="announcement-files-input">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<AttachFile />}
                    disabled={sendingAnnouncement}
                  >
                    {t('adminToolsPage.announcements.attachFiles')}
                  </Button>
                </label>
                {announcementFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {announcementFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                        onDelete={() => onRemoveFile(index)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Email Notification Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmailNotification}
                    onChange={(e) => onSendEmailNotificationChange(e.target.checked)}
                    disabled={sendingAnnouncement}
                  />
                }
                label={t('adminToolsPage.announcements.sendEmailNotification')}
                sx={{ mt: 1, mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={onSend}
                disabled={sendingAnnouncement}
                startIcon={sendingAnnouncement ? <CircularProgress size={16} /> : <Send />}
                sx={{
                  backgroundColor: BRAND_COLORS.forestGreen.main,
                  '&:hover': {
                    backgroundColor: BRAND_COLORS.forestGreen.main,
                    opacity: 0.8,
                  },
                }}
              >
                {sendingAnnouncement
                  ? t('adminToolsPage.announcements.sending')
                  : t('adminToolsPage.announcements.sendAnnouncement')}
              </Button>

              {sendAnnouncementError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {sendAnnouncementError}
                </Alert>
              )}

              {sendAnnouncementResult && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {t('adminToolsPage.announcements.sentSuccess', {
                    count: sendAnnouncementResult.recipient_count,
                  })}
                </Alert>
              )}
            </Box>
          </Paper>

          {/* Sent Announcements List */}
          {announcementsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : announcementsError ? (
            <Alert severity="error">{announcementsError}</Alert>
          ) : announcementsData && announcementsData.announcements.length > 0 ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('adminToolsPage.announcements.sentAnnouncements', {
                  count: announcementsData.announcements.length,
                })}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('adminToolsPage.announcements.tableHeaders.title')}</TableCell>
                      <TableCell>
                        {t('adminToolsPage.announcements.tableHeaders.created')}
                      </TableCell>
                      <TableCell>
                        {t('adminToolsPage.announcements.tableHeaders.recipients')}
                      </TableCell>
                      <TableCell>
                        {t('adminToolsPage.announcements.tableHeaders.readUnread')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {announcementsData.announcements.map((announcement: AnnouncementAdmin) => (
                      <TableRow key={announcement.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {announcement.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {announcement.body.substring(0, 100)}
                            {announcement.body.length > 100 ? '...' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTimeISO(announcement.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('adminToolsPage.announcements.by', {
                              name: announcement.created_by.name || announcement.created_by.email,
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={announcement.recipient_stats.total}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={t('adminToolsPage.announcements.read', {
                                count: announcement.recipient_stats.read,
                              })}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                            <Chip
                              label={t('adminToolsPage.announcements.unread', {
                                count: announcement.recipient_stats.unread,
                              })}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="info">{t('adminToolsPage.announcements.noAnnouncementsYet')}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
